import { WithCleanup } from "../async/WithCleanup"
import { IsNull } from "../checks/IsNull"

type WaitQueue = Array<() => void>

/**
 * Creates a lock manager for mutual exclusion on tasks identified by generic keys.
 * Ensures only one task with the same ID executes at a time, queuing others.
 *
 * @example
 * ```ts
 * import { LockManager } from "@pdfking/tfunc"
 *
 * const locks = LockManager<string>()
 *
 * // Manually acquire and release
 * const release = await locks.acquire("resource-1")
 * try {
 *   // Critical section
 * } finally {
 *   release()
 * }
 *
 * // Or use schedule for automatic cleanup
 * const result = await locks.schedule("resource-1", async () => {
 *   return await fetchData()
 * })

 * ```
 */
export const LockManager = <T = string>() => {
  const locks = new Map<T, WaitQueue>()

  const create_release = (task_id: T, queue: WaitQueue): (() => void) => {
    return () => {
      const next = queue.shift()
      if (next) {
        next()
      } else {
        locks.delete(task_id)
      }
    }
  }

  const waiting = (): number => {
    let total = 0
    for (const queue of locks.values()) {
      total += queue.length
    }
    return total
  }

  const acquire = (task_id: T): Promise<() => void> => {
    return new Promise<() => void>(resolve => {
      let queue = locks.get(task_id)

      const release_func = () => resolve(create_release(task_id, queue!))

      if (IsNull(queue)) {
        queue = []
        locks.set(task_id, queue)
        return release_func()
      }
      queue.push(release_func)
    })
  }

  const schedule = async <G>(
    task_id: T,
    func: () => Promise<G>
  ): Promise<G> => {
    const release = await acquire(task_id)
    return WithCleanup(func(), release)
  }

  return { acquire, schedule, waiting }
}
